import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Select } from 'antd';
import walletApi from 'api/walletApi';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import SuccessIcon from 'assets/icons/SuccessIcon';
import apple_logo from 'assets/icons/icon_apple_logo.png';
import google_play from 'assets/icons/icon_google_play.png';
import { showErrorModal } from 'components/Modals/CommonModals';
import Typo from 'consts/typography';
import useUserDetail from 'hooks/useUserDetail';
import { CreateVinpayOrderResponseModel } from 'models/wallet';
import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import OrderQrTab from './OrderQrTab';
import { isValidAmount } from './WithdrawModal';

interface FiatDepositTabProps {
  closeModal: () => void;
  resetTab: () => void;
}

interface TypeDepositOption {
  value: number;
  label: ReactNode;
}

const FiatDepositTab: React.FunctionComponent<FiatDepositTabProps> = (props) => {
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const { isUserLoggedIn } = useUserDetail();
  const t = useAppTranslation();
  const et = useErrTranslation();

  const [typeDeposit, setTypeDeposit] = useState<number>(1);
  const [amount, setAmount] = useState<string>('');
  const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
  const [isShowQr, setIsShowQr] = useState<boolean>(false);
  const [qrCode, setQrCode] = useState<CreateVinpayOrderResponseModel | null>(null);

  const amountRef = useRef('');

  useEffect(() => {
    amountRef.current = amount;
  }, [amount]);

  const createOrder = async () => {
    const { ok, body } = await walletApi.createOrder({ amount: Number(amountRef.current) });
    if (ok) {
      return body?.id || '';
    }
    return '';
  };

  const createVinpayOrder = async () => {
    const { ok, body } = await walletApi.createVinpayOrder({ amount: Number(amountRef.current) });
    if (ok && body) {
      setQrCode(body);
      setIsShowQr(true);
    }
  };

  const onApprove = async () => {
    setIsShowSuccess(true);
  };

  const onError = async () => {
    showErrorModal({
      content: et('error.deposit.message'),
      title: et('error.deposit.title'),
    });
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const onCloseModal = () => {
    resetModal();
    props.resetTab();
    props.closeModal();
  };

  const resetModal = () => {
    setAmount('');
    setIsShowSuccess(false);
    setQrCode(null);
    setIsShowQr(false);
  };

  const handleTypeDepositChange = (event: number) => {
    setTypeDeposit(event);
  };

  const typeDepositOptions: TypeDepositOption[] = useMemo(() => {
    return [
      {
        value: 1,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            Vinpay
          </span>
        ),
      },
      {
        value: 2,
        label: (
          <span
            className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
          >
            Paypal
          </span>
        ),
      },
    ];
  }, []);

  const hiddenQrCode = () => {
    setIsShowQr(false);
    setAmount('');
  };

  return (
    <div className="w-80 flex flex-col gap-3 items-center">
      {!isShowSuccess ? (
        <Fragment>
          {typeDeposit === 1 && (
            <div className="vinpay-links">
              <a
                className="vinpay-link"
                href={process.env.REACT_APP_LINK_DOWNLOAD_VINPAY_IOS}
                target="_blank"
                rel="noreferrer"
              >
                <img className="vinpay-link__left" src={apple_logo} alt="apple logo" />
                <div className="vinpay-link__right">
                  <span className="vinpay-link__right-up">Download on the</span>
                  <span className="vinpay-link__right-down">App Store</span>
                </div>
              </a>
              <a
                className="vinpay-link"
                href={process.env.REACT_APP_LINK_DOWNLOAD_VINPAY_ANDROID}
                target="_blank"
                rel="noreferrer"
              >
                <img className="vinpay-link__left" src={google_play} alt="apple logo" />
                <div className="vinpay-link__right">
                  <span className="vinpay-link__right-up">GET IT ON</span>
                  <span className="vinpay-link__right-down">Google Play</span>
                </div>
              </a>
            </div>
          )}
          {isShowQr ? (
            <OrderQrTab
              visible={isShowQr}
              qrCode={qrCode}
              hiddenQrCode={hiddenQrCode}
              closeModal={onCloseModal}
            />
          ) : (
            <Fragment>
              <div className="common-select w-100">
                <p
                  className={`primary-text-color ${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  }`}
                >
                  {t('deposit.fiat.type')}
                </p>
                <Select
                  className="antd__select"
                  dropdownClassName="antd__select-dropdown"
                  value={typeDeposit}
                  onChange={handleTypeDepositChange}
                  options={typeDepositOptions}
                />
              </div>
              <div className="common-input input-amount w-100">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                >
                  {t('cashier.amount')}
                </label>
                <input
                  type="text"
                  name="amount"
                  id="amount"
                  autoComplete="off"
                  onChange={onChange}
                  value={amount}
                  placeholder={t('placeholder.enterAmount')}
                />
              </div>
              {isUserLoggedIn && typeDeposit === 2 && (
                <PayPalScriptProvider
                  options={{
                    clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
                    currency: 'USD',
                    intent: 'capture',
                    components: 'buttons',
                  }}
                >
                  <PayPalButtons
                    className="w-80"
                    // disabled={Number(amount) === 0}
                    disabled={true}
                    style={{ label: 'checkout', height: 34 }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                  />
                </PayPalScriptProvider>
              )}

              {isUserLoggedIn && typeDeposit === 1 && (
                <div className="withdraw-balance">
                  <div className="withdraw-balance__left"></div>
                  <div className="withdraw-balance__right">
                    <button
                      className={`button-generate ${isUserLoggedIn && amount ? '' : 'disabled'}`}
                      type="submit"
                      disabled={!isUserLoggedIn || !amount}
                      onClick={createVinpayOrder}
                    >
                      {t('deposit.fiat.generate')}
                    </button>
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </Fragment>
      ) : (
        <div className="center-content-col gap-3 modal--success text-center">
          <SuccessIcon width="40" height="40" />
          <p className="title">{t('deposit.success.title')}</p>
          <p className="message">{`${t('deposit.success.message')}`}</p>
          <button type="button" className="continue" onClick={onCloseModal}>
            {t('deposit.close')}
          </button>
        </div>
      )}
    </div>
  );
};

export default FiatDepositTab;
